import React, { createContext, useContext, useEffect, useState } from 'react';
import { loadBrandConfig, BrandConfig } from '../branding/loadBrand';

const BrandContext = createContext<BrandConfig | undefined>(undefined);
export const useBrand = () => {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within BrandProvider');
  return ctx;
};

const BYZFUNDER_DEFAULTS = {
  '--color-primary':  '#003F30', // Moss
  '--color-secondary':'#A1E888', // Lime
  '--color-accent':   '#D0E0C0', // Mint
  '--logo-url':       'url("/assets/byzfunder/frontPageLogo.svg")',
  '--login-bg-url':   'url("./assets/background.svg")',
} as const;

const BYZWASH_DEFAULTS = {
  '--color-primary':  '#1CA0C6',
  '--color-secondary':'#51D5FF',
  '--color-accent':   '#E8FBFF',
  '--logo-url':       'url("/assets/byzwash/logo.svg")',
  '--login-bg-url':   'url("/assets/background.svg")',
} as const;

function setCssVars(vars: Record<string, string>) {
  const root = document.documentElement;
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
}

function clearCssVars(keys: string[]) {
  const root = document.documentElement;
  keys.forEach(k => root.style.removeProperty(k));
}

function applyCssVars(brand: BrandConfig) {
  const root = document.documentElement;

  const slug =
    (brand.company?.slug?.toLowerCase?.() as 'byzfunder' | 'byzwash' | undefined) ??
    (brand as any).slug ??
    'byzfunder';

  root.setAttribute('data-brand', slug);

  const base =
    slug === 'byzwash' ? BYZWASH_DEFAULTS : BYZFUNDER_DEFAULTS;

  setCssVars(base);

  if (brand.palette?.primary)   root.style.setProperty('--color-primary', brand.palette.primary);
  if (brand.palette?.secondary) root.style.setProperty('--color-secondary', brand.palette.secondary);
  if (brand.palette?.accent)    root.style.setProperty('--color-accent', brand.palette.accent);
  if (brand.logo_url)           root.style.setProperty('--logo-url', `url("${brand.logo_url}")`);
  if ((brand as any).login_bg_url) {
    root.style.setProperty('--login-bg-url', `url("${(brand as any).login_bg_url}")`);
  }

  root.style.setProperty('--ring', '165 100% 12%');
}

export const BrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [brand, setBrand] = useState<BrandConfig>();
  const pathname = window.location.pathname;

  useEffect(() => {
    (async () => {
      const cfg = await loadBrandConfig();
      setBrand(cfg);
      applyCssVars(cfg);
      if (cfg.company?.name) document.title = `${cfg.company.name} Customer Portal`;
      if (pathname.includes("/online-checkout") || pathname.includes("/secondary-checkout")) {
        document.title = `${cfg.company.name} Online Checkout Portal`;
        document.documentElement.setAttribute("font-brand", 'online-checkout');
      }
    })();
      }, []);

  if (!brand) return null;

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
};

export type BrandSlug = 'byzfunder' | 'byzwash';

export function detectBrandSlug(hostname: string, qsBrand?: string | null): BrandSlug {
  const forced = (qsBrand || '').toLowerCase();
  if (forced === 'byzwash' || forced === 'byzfunder') return forced as BrandSlug;

  const h = (hostname || '').toLowerCase();
  if (h.includes('byzwash')) return 'byzwash';
  return 'byzfunder';
}
